import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { ComponentType } from "react";
import NotFound from "./pages/NotFound";
import { Redirect, Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ConversionTracker } from "./components/ConversionTracker";
import { GoogleAnalytics } from "./components/GoogleAnalytics";
import { Head } from "./components/Head";
import { SiteLayout } from "./components/SiteLayout";
import { ThemeProvider } from "./contexts/ThemeContext";
import AccessoryCategory from "./pages/AccessoryCategory";
import AccessoryQuoteCart from "./pages/AccessoryQuoteCart";
import BuyerGuide from "./pages/BuyerGuide";
import BuyerGuides from "./pages/BuyerGuides";
import ColorsFinishes from "./pages/ColorsFinishes";
import Contact from "./pages/Contact";
import CityServiceArea from "./pages/CityServiceArea";
import Faq from "./pages/Faq";
import Home from "./pages/Home";
import MaterialDetail from "./pages/MaterialDetail";
import MeasuringGuide from "./pages/MeasuringGuide";
import MountingStyles from "./pages/MountingStyles";
import NewYorkCity from "./pages/NewYorkCity";
import Partitions from "./pages/Partitions";
import PartitionBudgetCalculator from "./pages/PartitionBudgetCalculator";
import Projects from "./pages/Projects";
import PrivacyOptions from "./pages/PrivacyOptions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RequestQuote from "./pages/RequestQuote";
import Resources from "./pages/Resources";
import ServiceAreas from "./pages/ServiceAreas";
import StateServiceArea from "./pages/StateServiceArea";
import { SubAssembliesHub, SubAssemblyDetail } from "./pages/SubAssemblies";
import { TechnicalResourceDetail, TechnicalResourcesLibrary } from "./pages/TechnicalResources";
import TermsOfUse from "./pages/TermsOfUse";
import WhyUs from "./pages/WhyUs";

type AppShellProps = {
  AccessoryCatalog: ComponentType;
  HardwareCategoryPage: ComponentType;
  HardwareProductPage: ComponentType;
};

function Router({ AccessoryCatalog, HardwareCategoryPage, HardwareProductPage }: AppShellProps) {
  const AccessoryCatalogRoute = () => <AccessoryCatalog />;
  const HardwareCategoryRoute = () => <HardwareCategoryPage />;
  const HardwareProductRoute = () => <HardwareProductPage />;
  const LegacyHardwareRoot = () => <Redirect to="/commercial-restroom-accessories" replace />;

  return (
    <SiteLayout>
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/bathroom-partitions" component={Partitions} />
        <Route path="/bathroom-partitions/:slug" component={MaterialDetail} />
        <Route path="/partition-budget-calculator" component={PartitionBudgetCalculator} />
        <Route path="/commercial-restroom-accessories" component={AccessoryCatalogRoute} />
        <Route path="/commercial-restroom-accessories/:category" component={AccessoryCategory} />
        <Route path="/accessory-quote-cart" component={AccessoryQuoteCart} />
        <Route path="/hardware" component={LegacyHardwareRoot} />
        <Route path="/hardware/:category/:product" component={HardwareProductRoute} />
        <Route path="/hardware/:category" component={HardwareCategoryRoute} />
        <Route path="/sub-assemblies" component={SubAssembliesHub} />
        <Route path="/sub-assemblies/:slug" component={SubAssemblyDetail} />
        <Route path="/technical-resources" component={TechnicalResourcesLibrary} />
        <Route path="/technical-resources/:slug" component={TechnicalResourceDetail} />
        <Route path="/resources/buyer-guides" component={BuyerGuides} />
        <Route path="/resources/buyer-guides/:slug" component={BuyerGuide} />
        <Route path="/resources" component={Resources} />
        <Route path="/resources/mounting-styles" component={MountingStyles} />
        <Route path="/resources/privacy-options" component={PrivacyOptions} />
        <Route path="/resources/how-to-measure" component={MeasuringGuide} />
        <Route path="/resources/colors-finishes" component={ColorsFinishes} />
        <Route path="/resources/faqs" component={Faq} />
        <Route path="/why-partition-stalls" component={WhyUs} />
        <Route path="/projects" component={Projects} />
        <Route path="/contact-partition-stalls" component={Contact} />
        <Route path="/service-areas" component={ServiceAreas} />
        <Route path="/service-areas/new-york-city" component={NewYorkCity} />
        <Route path="/service-areas/:stateSlug/:citySlug" component={CityServiceArea} />
        <Route path="/service-areas/:slug" component={StateServiceArea} />
        <Route path="/request-a-quote" component={RequestQuote} />
        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-use" component={TermsOfUse} />
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </SiteLayout>
  );
}

export default function AppShell(props: AppShellProps) {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Head />
          <GoogleAnalytics />
          <ConversionTracker />
          <Toaster />
          <Router {...props} />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
